import type { Destructor } from "@suse-o11y/destructor";
import { msPerFrame } from "@suse-o11y/ms-per-frame";
import debounce_ from "lodash/debounce";

const DEFAULT_DEBOUNCE = msPerFrame(60);

export interface OnResizeOptions<TNode> {
	node: TNode;

	/**
	 * Debounce time in milliseconds when number, or onIdle when "on-idle".
	 *
	 * @default 60fps
	 */
	debounce?: number;
}

export function onResize<TNode extends Element>(
	handler: (node: TNode, entry: ResizeObserverEntry) => void | Destructor,
	{ node, debounce = DEFAULT_DEBOUNCE }: OnResizeOptions<TNode>,
): Destructor {
	let cleanup: void | Destructor;

	if (debounce === 0) {
		const resizeObserver = new ResizeObserver(([entry]) => {
			cleanup?.();
			cleanup = handler(node, entry);
		});

		resizeObserver.observe(node);

		return () => {
			resizeObserver.disconnect();
		};
	}

	const listener = debounce_(([entry]: [ResizeObserverEntry]) => {
		cleanup?.();
		cleanup = handler(node, entry);
	}, debounce);

	const resizeObserver = new ResizeObserver(listener);

	resizeObserver.observe(node);

	return () => {
		resizeObserver.disconnect();
		listener.cancel();
		cleanup?.();
	};
}
